export type ActivityStats = {
  currentStreak: number;
  longestStreak: number;
  activeDaysLast30: number;
  activeDaysThisMonth: number;
  uniqueDays: number;
  lastActivityDay?: string; // YYYY-MM-DD
};

type EvidenceLike = {
  created_on?: string;
  createdAt?: string;
  date?: string;
};

function toDayKey(d: Date): string {
  // Use UTC day key to avoid locale surprises in streak math.
  return d.toISOString().slice(0, 10);
}

function dayDiff(a: string, b: string): number {
  // a - b in days (both YYYY-MM-DD)
  const ad = new Date(a + "T00:00:00Z").getTime();
  const bd = new Date(b + "T00:00:00Z").getTime();
  return Math.round((ad - bd) / (1000 * 60 * 60 * 24));
}

export function computeActivityStats(evidences: EvidenceLike[] = []): ActivityStats {
  const daySet = new Set<string>();

  for (const ev of evidences || []) {
    const raw = ev?.created_on || ev?.createdAt || ev?.date;
    if (!raw) continue;
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) continue;
    daySet.add(toDayKey(dt));
  }

  const days = Array.from(daySet).sort(); // ascending

  let currentStreak = 0;
  let longestStreak = 0;

  if (days.length > 0) {
    // Longest streak
    let run = 1;
    longestStreak = 1;
    for (let i = 1; i < days.length; i++) {
      if (dayDiff(days[i], days[i - 1]) === 1) {
        run += 1;
      } else {
        run = 1;
      }
      if (run > longestStreak) longestStreak = run;
    }

    // Current streak (ending on most recent activity day)
    currentStreak = 1;
    for (let i = days.length - 1; i > 0; i--) {
      if (dayDiff(days[i], days[i - 1]) === 1) {
        currentStreak += 1;
      } else {
        break;
      }
    }
  }

  const now = new Date();
  const todayKey = toDayKey(now);
  const start30 = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
  const start30Key = toDayKey(start30);

  const thisMonth = now.getUTCMonth();
  const thisYear = now.getUTCFullYear();

  const activeDaysLast30 = days.filter((d) => d >= start30Key && d <= todayKey).length;
  const activeDaysThisMonth = days.filter((d) => {
    const dt = new Date(d + "T00:00:00Z");
    return dt.getUTCFullYear() === thisYear && dt.getUTCMonth() === thisMonth;
  }).length;

  return {
    currentStreak,
    longestStreak,
    activeDaysLast30,
    activeDaysThisMonth,
    uniqueDays: days.length,
    lastActivityDay: days.length ? days[days.length - 1] : undefined,
  };
}
